import { useState } from "react";
import { NavLink, Outlet, useLocation, useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2 as OrganizationsIcon,
  FlaskConical as BetaFeaturesIcon,
  LayoutDashboard as DashboardIcon,
  LogOut as SignOutIcon,
  Menu as MenuIcon,
  ScrollText as LogsIcon,
  Settings as SettingsIcon,
  Shield as ShieldIcon,
  Users as UsersIcon,
  X as CloseIcon,
  type LucideIcon,
} from "lucide-react";
import type { SuperAdminSelfResponse } from "@multi-chat/shared";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher/index.js";
import { AppBrandMark } from "../../components/layout/AppBrandMark.js";
import { RailAvatarBadge } from "../../components/layout/RailAvatarBadge.js";
import {
  CHARCOAL_FOREST_SURFACE,
  RAIL_FOCUS,
  RAIL_HEADER_CONTROL,
  RAIL_SCROLL,
  railActiveIndicatorClassName,
} from "../../components/layout/rail-compact.js";
import { useMinWidth } from "../../components/layout/use-min-width.js";
import { FeedBotMark } from "../../components/brand/FeedBotMark.js";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "../../components/ui/sheet/index.js";
import { SimpleTooltip, TooltipProvider } from "../../components/ui/tooltip/index.js";
import { motion, useReducedMotion } from "../../lib/motion.js";
import { cn } from "../../lib/utils.js";
import { NotificationStack } from "../notifications/NotificationStack.js";
import { NotificationProvider } from "../notifications/notification-context.js";
import { markFromName } from "../organizations/organization-mark.js";
import { OrganizationSwitcher } from "../organizations/OrganizationSwitcher/OrganizationSwitcher.js";

const DESKTOP_MIN_PX = 1200;
const TABLET_MIN_PX = 768;

interface ConsoleNavItem {
  to: string;
  end: boolean;
  labelKey: string;
  icon: LucideIcon;
}

const NAV: readonly ConsoleNavItem[] = [
  { to: "/super-admin", end: true, labelKey: "superAdmin.nav.dashboard", icon: DashboardIcon },
  { to: "/super-admin/organizations", end: false, labelKey: "superAdmin.nav.organizations", icon: OrganizationsIcon },
  { to: "/super-admin/beta-features", end: false, labelKey: "superAdmin.nav.betaFeatures", icon: BetaFeaturesIcon },
  { to: "/super-admin/users", end: false, labelKey: "superAdmin.nav.users", icon: UsersIcon },
  { to: "/super-admin/logs", end: false, labelKey: "superAdmin.nav.logs", icon: LogsIcon },
  { to: "/super-admin/settings", end: false, labelKey: "superAdmin.nav.settings", icon: SettingsIcon },
];

const CONSOLE_SURFACE = `font-feedbot ${CHARCOAL_FOREST_SURFACE} text-[14px] leading-[1.5] text-[#eef6f3] before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-[linear-gradient(90deg,transparent,#55bca6b3,transparent)] before:content-['']`;

const CONSOLE_TOP_BAR =
  "font-feedbot relative z-[5] flex min-h-14 shrink-0 items-center gap-3 bg-[linear-gradient(90deg,#1c2b2b,#152323)] px-3 py-2 pt-[max(0.5rem,env(safe-area-inset-top,0px))] ps-[max(0.75rem,env(safe-area-inset-left,0px))] pe-[max(0.75rem,env(safe-area-inset-right,0px))] text-[#eef6f3] shadow-[0_6px_20px_rgba(8,18,18,0.16)] after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-[linear-gradient(90deg,transparent,#55bca680,transparent)] after:content-['']";

const SECTION_HEADING =
  "m-0 px-[11px] pt-[9px] pb-1.5 text-[10px] leading-[1.5] font-[750] tracking-[0.08em] text-[#7d9a93]";

const ROW = `relative my-0.5 flex min-h-11 w-full items-center rounded-[10px] text-start font-[650] no-underline transition-[background-color,color,box-shadow] duration-[160ms] motion-reduce:transition-none ${RAIL_FOCUS}`;
const ROW_EXPANDED = "gap-[13px] px-[13px] py-3";
const ROW_COMPACT = "justify-center p-[13px]";
const ROW_IDLE = "text-[#a9bcb8] hover:bg-white/[0.05] hover:text-white";
const ROW_ACTIVE =
  "bg-[linear-gradient(90deg,rgba(85,188,166,0.1),rgba(85,188,166,0.18))] text-[#f4fffb] shadow-[inset_0_0_0_1px_rgba(108,225,180,0.12)]";

const SIGN_OUT_ROW =
  "flex min-h-10 w-full items-center gap-2.5 rounded-[10px] border border-transparent bg-transparent px-2.5 text-start text-[13px] font-[650] text-[#f0a59c] transition-[background-color,border-color,color] duration-150 ease-out hover:border-[rgba(240,165,156,0.18)] hover:bg-[rgba(240,120,108,0.1)] hover:text-[#ffc2ba] motion-reduce:transition-none";

function ConsoleNavLink({
  item,
  compact,
  reduceMotion,
  onNavigate,
}: {
  item: ConsoleNavItem;
  compact: boolean;
  reduceMotion: boolean;
  onNavigate?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const Icon = item.icon;
  const label = t(item.labelKey);
  const isActive = useMatch({ path: item.to, end: item.end }) !== null;
  // String className and children only: the compact tooltip's Slot cannot merge render functions.
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      aria-label={compact ? label : undefined}
      className={cn(ROW, compact ? ROW_COMPACT : ROW_EXPANDED, isActive ? ROW_ACTIVE : ROW_IDLE)}
    >
      {isActive ? (
        <motion.span
          layoutId="super-admin-active-indicator"
          className={railActiveIndicatorClassName(compact)}
          transition={{ duration: reduceMotion ? 0 : 0.16, ease: "easeOut" }}
          aria-hidden="true"
        />
      ) : null}
      <Icon
        size={compact ? 21 : 19}
        strokeWidth={1.8}
        className={cn("shrink-0 transition-colors duration-150", isActive && "text-[#6ce1b4]")}
        aria-hidden="true"
      />
      {compact ? null : <span className="min-w-0 truncate">{label}</span>}
    </NavLink>
  );

  return compact ? <SimpleTooltip content={label}>{link}</SimpleTooltip> : link;
}

function ConsoleAccount({
  admin,
  compact,
}: {
  admin: SuperAdminSelfResponse;
  compact: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const avatar = (
    <span
      className="relative inline-grid size-[35px] shrink-0 place-items-center rounded-[11px] bg-[linear-gradient(140deg,#5ccba4,#2c7a68)] text-[12px] font-[800] text-white"
      aria-hidden="true"
    >
      {markFromName(admin.displayName)}
      <RailAvatarBadge icon={ShieldIcon} access="super-admin" />
    </span>
  );

  if (compact) {
    return (
      <div
        className="flex w-full justify-center py-1"
        role="group"
        aria-label={`${t("superAdmin.shell.account")}: ${admin.displayName}`}
        title={admin.displayName}
        data-testid="super-admin-account"
      >
        {avatar}
      </div>
    );
  }

  return (
    <div
      className="flex min-w-0 items-center gap-[11px] px-1.5 py-1"
      role="group"
      aria-label={t("superAdmin.shell.account")}
      data-testid="super-admin-account"
    >
      {avatar}
      <span className="grid min-w-0 flex-1 text-start">
        <span className="truncate text-[12.5px] leading-[1.5] font-[750] text-[#f2fbf8]" title={admin.displayName}>
          <bdi>{admin.displayName}</bdi>
        </span>
        <span className="truncate text-[11px] leading-[1.5] text-[#8fa9a3]" title={admin.email}>
          <bdi dir="ltr">{admin.email}</bdi>
        </span>
      </span>
    </div>
  );
}

function ConsoleRail({
  admin,
  compact,
  headerControl,
  onLogout,
  onNavigate,
  footerClassName,
}: {
  admin: SuperAdminSelfResponse;
  compact: boolean;
  headerControl?: React.ReactNode;
  onLogout: () => void;
  onNavigate?: () => void;
  footerClassName?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const signOutLabel = t("app.signOut");

  return (
    <>
      <div
        className={cn(
          "flex min-h-[84px] shrink-0 border-b border-white/[0.07]",
          compact
            ? "mx-2 flex-col-reverse items-center justify-center gap-2"
            : "mx-[18px] items-center justify-between gap-2.5 rtl:flex-row-reverse",
        )}
      >
        {headerControl}
        <AppBrandMark compact={compact} />
      </div>
      <div
        className={cn(
          "min-h-0 flex-1 overflow-x-hidden overflow-y-auto py-4",
          compact ? "px-2" : "px-3",
          RAIL_SCROLL,
        )}
      >
        <div className={compact ? "mb-[18px]" : "mx-px mb-5"}>
          <OrganizationSwitcher platformAdmin compact={compact} onSelect={onNavigate} />
        </div>
        <nav className="flex min-w-0 flex-col" aria-label={t("superAdmin.badge")}>
          <p className={compact ? "sr-only" : SECTION_HEADING}>{t("superAdmin.shell.navHeading")}</p>
          {NAV.map((item) => (
            <ConsoleNavLink
              key={item.to}
              item={item}
              compact={compact}
              reduceMotion={reduceMotion}
              onNavigate={onNavigate}
            />
          ))}
        </nav>
      </div>
      <div
        className={cn(
          "flex min-w-0 shrink-0 flex-col gap-2 border-t border-white/[0.07] bg-black/[0.12]",
          compact ? "items-center px-2 py-3" : "p-[13px]",
          footerClassName,
        )}
      >
        <ConsoleAccount admin={admin} compact={compact} />
        {compact ? <LanguageSwitcher variant="rail-collapsed" /> : <LanguageSwitcher variant="rail" />}
        {compact ? (
          <SimpleTooltip content={signOutLabel}>
            <button
              type="button"
              onClick={onLogout}
              className={cn(SIGN_OUT_ROW, "size-10 justify-center px-0", RAIL_FOCUS)}
              aria-label={signOutLabel}
              data-testid="super-admin-sign-out"
            >
              <SignOutIcon size={18} strokeWidth={1.8} className="rtl:-scale-x-100" aria-hidden="true" />
            </button>
          </SimpleTooltip>
        ) : (
          <button
            type="button"
            onClick={onLogout}
            className={cn(SIGN_OUT_ROW, RAIL_FOCUS)}
            data-testid="super-admin-sign-out"
          >
            <SignOutIcon size={17} strokeWidth={1.8} className="shrink-0 rtl:-scale-x-100" aria-hidden="true" />
            <span className="min-w-0 truncate">{signOutLabel}</span>
          </button>
        )}
      </div>
    </>
  );
}

export function SuperAdminShell({
  admin,
  onLogout,
}: {
  admin: SuperAdminSelfResponse;
  onLogout: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const settingsWorkspace = location.pathname.startsWith("/super-admin/settings") || /^\/super-admin\/organizations\/\d+/.test(location.pathname);
  const isDesktop = useMinWidth(DESKTOP_MIN_PX);
  const showRail = useMinWidth(TABLET_MIN_PX);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <NotificationProvider>
    <TooltipProvider delayDuration={300}>
    <div className="relative flex h-dvh min-h-0 overflow-hidden bg-canvas" data-testid="super-admin-shell">
      {showRail ? (
        <aside
          className={cn(
            CONSOLE_SURFACE,
            "relative z-[6] flex h-dvh shrink-0 flex-col overflow-hidden",
            "ltr:shadow-[8px_0_30px_rgba(8,18,18,0.1)] rtl:shadow-[-8px_0_30px_rgba(8,18,18,0.1)]",
            isDesktop ? "w-[254px]" : "w-[74px]",
          )}
          aria-label={t("superAdmin.badge")}
          data-testid="super-admin-rail"
        >
          <ConsoleRail admin={admin} compact={!isDesktop} onLogout={onLogout} />
        </aside>
      ) : null}

      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {!showRail ? (
          <header className={CONSOLE_TOP_BAR} data-testid="super-admin-top-bar">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen} modal={false}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "inline-grid size-10 shrink-0 place-items-center rounded-[12px] border border-white/10 bg-white/[0.06] text-[#d9ebe6] transition-[background-color,border-color,transform] duration-150 ease-out hover:border-white/20 hover:bg-white/10 active:scale-[0.94] active:bg-white/10 motion-reduce:transition-none motion-reduce:active:scale-100",
                    RAIL_FOCUS,
                  )}
                  aria-label={t("app.menu")}
                >
                  <MenuIcon size={20} strokeWidth={1.8} aria-hidden="true" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="start"
                className={cn(CONSOLE_SURFACE, "w-[254px] max-w-[85vw] overflow-hidden")}
                aria-describedby={undefined}
              >
                <SheetTitle className="sr-only">{t("superAdmin.badge")}</SheetTitle>
                <ConsoleRail
                  admin={admin}
                  compact={false}
                  headerControl={
                    <SheetClose asChild>
                      <button
                        type="button"
                        className={cn(RAIL_HEADER_CONTROL, "h-[30px]")}
                        aria-label={t("app.closeMenu")}
                      >
                        <CloseIcon size={16} strokeWidth={1.8} aria-hidden="true" />
                      </button>
                    </SheetClose>
                  }
                  onLogout={onLogout}
                  onNavigate={() => setMobileOpen(false)}
                  footerClassName="pb-[max(13px,env(safe-area-inset-bottom,0px))]"
                />
              </SheetContent>
            </Sheet>
            <FeedBotMark className="size-7 shrink-0" label={t("app.name")} />
            <span className="h-6 w-px shrink-0 bg-white/[0.12]" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="m-0 truncate text-[10px] leading-4 font-[750] tracking-[0.08em] text-[#7d9a93]">
                {t("superAdmin.shell.navHeading")}
              </p>
              <p className="m-0 flex min-w-0 items-center gap-1.5 text-[14.5px] leading-5 font-[700] text-[#f2fbf8]">
                <ShieldIcon size={14} strokeWidth={2} className="shrink-0 text-[#6ce1b4]" aria-hidden="true" />
                <span className="truncate">{t("superAdmin.badge")}</span>
              </p>
            </div>
          </header>
        ) : null}

        <div
          className={
            settingsWorkspace
              ? "flex min-h-0 flex-1 flex-col overflow-hidden"
              : "min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]"
          }
        >
          {settingsWorkspace ? (
            <div className="min-h-0 flex-1">
              <Outlet />
            </div>
          ) : (
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 py-5 md:px-6">
              <Outlet />
            </div>
          )}
        </div>
      </div>
      <NotificationStack />
    </div>
    </TooltipProvider>
    </NotificationProvider>
  );
}
